import { Slider } from "antd";
import { Lock, Unlock } from "lucide-react";
import {
  CALCULATOR_RANGES,
  BONUS_THRESHOLD_PCT,
  EARNINGS_ASSUMPTIONS,
} from "commons/earnings.constants";
import {
  calculateEarnings,
  formatInr,
  formatInrCompact,
  formatCount,
} from "services/earnings.service";

/**
 * The two-slider revenue estimate.
 *
 * Fully controlled so the page above can keep the numbers in the URL — which is
 * what makes every session shareable. All arithmetic comes from
 * `services/earnings.service`, shared with every other surface.
 */
function RevenueCalculator({
  members,
  bookingRate,
  onMembersChange,
  onBookingRateChange,
  footer,
  showAssumptions = true,
}) {
  const result = calculateEarnings({ members, bookingRatePct: bookingRate });
  const { min: memberMin, max: memberMax, step: memberStep } =
    CALCULATOR_RANGES.members;
  const { min: rateMin, max: rateMax } = CALCULATOR_RANGES.bookingRate;

  const membersLabel =
    members >= memberMax ? `${formatCount(memberMax)}+` : formatCount(members);

  return (
    <div className="grid gap-5 lg:grid-cols-[1.05fr_0.95fr]">
      {/* ── Inputs ───────────────────────────────────────────────── */}
      <div className="rounded-3xl border border-border/60 bg-card p-6 md:p-8">
        <div className="space-y-9">
          <div>
            <div className="mb-2 flex items-baseline justify-between gap-4">
              <label
                htmlFor="calc-members"
                className="text-sm font-semibold text-foreground"
              >
                Members on ShoUp
              </label>
              <span className="font-display text-3xl font-bold tabular-nums text-[hsl(var(--brand-lavender))]">
                {membersLabel}
              </span>
            </div>
            <Slider
              id="calc-members"
              min={memberMin}
              max={memberMax}
              step={memberStep}
              value={members}
              onChange={onMembersChange}
              tooltip={{ open: false }}
              aria-label="Members on ShoUp"
            />
            <div className="flex justify-between text-xs tabular-nums text-muted-foreground">
              <span>{formatCount(memberMin)}</span>
              <span>{formatCount(memberMax)}+</span>
            </div>
          </div>

          <div>
            <div className="mb-2 flex items-baseline justify-between gap-4">
              <label
                htmlFor="calc-rate"
                className="text-sm font-semibold text-foreground"
              >
                Members who book in a month
              </label>
              <span className="font-display text-3xl font-bold tabular-nums text-[hsl(var(--brand-coral))]">
                {bookingRate}%
              </span>
            </div>
            <Slider
              id="calc-rate"
              min={rateMin}
              max={rateMax}
              value={bookingRate}
              onChange={onBookingRateChange}
              marks={{
                [BONUS_THRESHOLD_PCT]: {
                  label: (
                    <span className="whitespace-nowrap text-[11px] font-semibold text-foreground">
                      {BONUS_THRESHOLD_PCT}% unlocks the bonus
                    </span>
                  ),
                },
              }}
              tooltip={{ open: false }}
              aria-label="Share of members who book in a month"
            />
          </div>

          <dl className="space-y-3 border-t border-border/60 pt-6 text-sm">
            <div className="flex justify-between gap-4">
              <dt className="text-muted-foreground">Members booking each month</dt>
              <dd className="font-semibold tabular-nums text-foreground">
                {formatCount(result.transactingMembers)} of {formatCount(members)}
              </dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-muted-foreground">
                Transactions on ShoUp each month
              </dt>
              <dd className="font-semibold tabular-nums text-foreground">
                {formatCount(result.transactions)}
              </dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-muted-foreground">Your tier</dt>
              <dd className="font-semibold text-[hsl(var(--brand-lavender))]">
                {result.tier.name} · {Math.round(result.tier.share * 100)}% of
                platform fee
              </dd>
            </div>
          </dl>
        </div>
      </div>

      {/* ── Result ───────────────────────────────────────────────── */}
      <div className="offset-card flex flex-col bg-card p-6 md:p-8">
        <p className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">
          Your estimated earnings
        </p>

        <p className="mt-4 font-display text-5xl font-bold leading-none tabular-nums text-[hsl(var(--brand-coral))] md:text-6xl">
          {formatInr(result.monthly)}
        </p>
        <p className="mt-2 text-sm text-muted-foreground">per month</p>

        <p className="mt-4 flex items-baseline gap-2">
          <span className="font-display text-2xl font-bold tabular-nums text-[hsl(var(--brand-lavender))]">
            {formatInrCompact(result.annual)}
          </span>
          <span className="text-sm text-muted-foreground">a year</span>
        </p>

        <div className="mt-7 space-y-5 border-t border-border/60 pt-6">
          <div className="flex items-baseline justify-between gap-4">
            <div>
              <p className="text-sm font-semibold text-foreground">
                Share of platform fee
              </p>
              <p className="mt-0.5 text-xs text-muted-foreground">
                {Math.round(result.tier.share * 100)}% of{" "}
                {formatInr(EARNINGS_ASSUMPTIONS.netFeePerTransaction)} ={" "}
                {formatInr(result.perTransaction)} per transaction
              </p>
            </div>
            <p className="font-display text-xl font-bold tabular-nums text-foreground">
              {formatInr(result.feeShare)}
            </p>
          </div>

          <div className="flex items-baseline justify-between gap-4">
            <div>
              <p className="flex items-center gap-1.5 text-sm font-semibold text-foreground">
                {result.bonusUnlocked ? (
                  <Unlock className="h-3.5 w-3.5 text-[hsl(var(--brand-coral))]" />
                ) : (
                  <Lock className="h-3.5 w-3.5 text-muted-foreground" />
                )}
                Performance bonus
              </p>
              <p className="mt-0.5 text-xs text-muted-foreground">
                {result.bonusUnlocked
                  ? `Unlocked — ${BONUS_THRESHOLD_PCT}% of your members book`
                  : `Needs ${BONUS_THRESHOLD_PCT}% of members booking each month`}
              </p>
            </div>
            <p
              className={`font-display text-xl font-bold tabular-nums ${
                result.bonusUnlocked ? "text-foreground" : "text-muted-foreground"
              }`}
            >
              {result.bonusUnlocked ? formatInr(result.bonus) : "Locked"}
            </p>
          </div>
        </div>

        {footer && <div className="mt-auto pt-7">{footer}</div>}
      </div>

      {showAssumptions && (
        <p className="text-xs leading-relaxed text-muted-foreground lg:col-span-2">
          Estimates only. Based on a platform fee of{" "}
          {formatInr(EARNINGS_ASSUMPTIONS.netFeePerTransaction)} per transaction
          net of GST ({formatInr(EARNINGS_ASSUMPTIONS.grossFeePerSide)} from each
          side, gross), and one transaction a month per active member. Actual
          earnings depend on real activity on ShoUp. Fixed sponsorship support is
          agreed separately.
        </p>
      )}
    </div>
  );
}

export default RevenueCalculator;
